import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { adminApi, AdminLoginEvent } from '../api/admin';

const PAGE_SIZE = 50;

export default function AdminAudit() {
  const { getToken } = useAuth();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: AdminLoginEvent[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const t = await getToken();
        const r = await adminApi.loginAudit(t, page, PAGE_SIZE);
        setData({ items: r.items, total: r.total });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken, page]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div>
      <h1 style={{ color: 'var(--mi-navy)' }}>Login Audit</h1>
      {error && <div style={{ background: '#fee', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}
      {!data ? (
        <div>Loading…</div>
      ) : (
        <>
          <div style={{ marginBottom: 8, color: 'var(--mi-muted)', fontSize: 13 }}>
            {data.total.toLocaleString()} login{data.total === 1 ? '' : 's'} recorded
          </div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>When</th>
                <th style={th}>Source</th>
                <th style={th}>Display Name</th>
                <th style={th}>Email</th>
                <th style={th}>IP</th>
                <th style={th}>User Agent</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((ev) => (
                <tr key={ev.id} style={{ borderTop: '1px solid var(--mi-border)' }}>
                  <td style={td}>{new Date(ev.created_at).toLocaleString()}</td>
                  <td style={td}>
                    <span style={sourceBadge(ev.source)}>{ev.source}</span>
                  </td>
                  <td style={td}>{ev.display_name ?? '—'}</td>
                  <td style={td}>{ev.email ?? '—'}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{ev.ip ?? '—'}</td>
                  <td style={{ ...td, fontSize: 12, color: 'var(--mi-muted)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ev.user_agent ?? ''}>
                    {ev.user_agent ?? '—'}
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td style={td} colSpan={6}>No logins on this page.</td></tr>
              )}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} style={btn}>← Prev</button>
            <div style={{ fontSize: 13, color: 'var(--mi-muted)' }}>Page {page} of {totalPages}</div>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} style={btn}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
}

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, overflow: 'hidden' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', background: 'var(--mi-bg)', fontSize: 13, color: 'var(--mi-muted)', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 12px', fontSize: 14 };
const btn: React.CSSProperties = { background: '#fff', border: '1px solid var(--mi-border)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' };
const sourceBadge = (s: 'staff' | 'client'): React.CSSProperties => ({
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  background: s === 'staff' ? '#eef2ff' : '#fff1e0',
  color: s === 'staff' ? '#3b3b88' : '#b45a00',
});
